import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { helpdeskAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ScreenCapture from '../components/ScreenCapture';
import './CreateTicket.css';

function CreateTicket() {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const { actor } = useAuth();
    const fileInputRef = useRef(null);
    const companyInputRef = useRef(null);
    const companyDropdownRef = useRef(null);

    const [formData, setFormData] = useState({
        subject: '',
        category_id: '',
        department_id: '',
        sub_department_id: '',
        priority: 'medium',
        description: '',
        company_id: ''
    });
    // An ERP session's department is decided at sign-in and pinned. Set as soon
    // as the actor is known — before the departments list has even loaded — so
    // the field is never briefly empty and required.
    useEffect(() => {
        if (!actor?.department_id) return;
        setFormData(prev => (
            String(prev.department_id) === String(actor.department_id)
                ? prev
                : { ...prev, department_id: String(actor.department_id) }
        ));
    }, [actor]);

    const [categories, setCategories] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showScreenCapture, setShowScreenCapture] = useState(false);

    // Company state
    const [companies, setCompanies] = useState([]);
    const [companySearch, setCompanySearch] = useState('');
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [showCompanyDialog, setShowCompanyDialog] = useState(false);
    const [companyDialogName, setCompanyDialogName] = useState('');
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [companiesLoading, setCompaniesLoading] = useState(false);

    useEffect(() => {
        loadCategories();
        loadDepartments();
        loadCompanies();
    }, []);

    // Close company dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) {
                setShowCompanyDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load sub-departments when department changes
    useEffect(() => {
        const loadSubDepartments = async () => {
            if (formData.department_id) {
                try {
                    const response = await helpdeskAPI.getSubDepartments(formData.department_id);
                    if (response.data.success) {
                        setSubDepartments(response.data.sub_departments || []);
                    } else {
                        setSubDepartments([]);
                    }
                } catch (error) {
                    console.error('Failed to load sub-departments:', error);
                    setSubDepartments([]);
                }
            } else {
                setSubDepartments([]);
            }
            // Clear sub_department_id when department changes
            setFormData(prev => ({ ...prev, sub_department_id: '' }));
        };
        
        loadSubDepartments();
    }, [formData.department_id]);

    const loadCategories = async () => {
        try {
            const response = await helpdeskAPI.getCategories();
            if (response.data.success) {
                setCategories(response.data.categories);
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const loadDepartments = async () => {
        try {
            const response = await helpdeskAPI.getDepartments();
            if (response.data.success) {
                setDepartments(response.data.departments);
            }
        } catch (error) {
            console.error('Failed to load departments:', error);
        }
    };

    const loadCompanies = async () => {
        try {
            setCompaniesLoading(true);
            const response = await helpdeskAPI.getCompanies();
            if (response.data.success) {
                const companiesList = response.data.companies || [];
                setCompanies(companiesList);
                // Auto-select default company
                const defaultCompany = companiesList.find(c => c.is_default === 1);
                if (defaultCompany) {
                    setSelectedCompany(defaultCompany);
                    setCompanySearch(defaultCompany.company_name);
                    setFormData(prev => ({ ...prev, company_id: defaultCompany.id }));
                } else if (companiesList.length === 1) {
                    setSelectedCompany(companiesList[0]);
                    setCompanySearch(companiesList[0].company_name);
                    setFormData(prev => ({ ...prev, company_id: companiesList[0].id }));
                }
            }
        } catch (error) {
            console.error('Failed to load companies:', error);
        } finally {
            setCompaniesLoading(false);
        }
    };

    // Filter companies based on search text
    const filteredCompanies = companies.filter(c =>
        c.company_name.toLowerCase().includes(companySearch.toLowerCase())
    );

    // Check if typed text exactly matches any company
    const exactMatch = companies.find(c =>
        c.company_name.toLowerCase() === companySearch.trim().toLowerCase()
    );

    const handleCompanyInputChange = (e) => {
        const value = e.target.value;
        setCompanySearch(value);
        setShowCompanyDropdown(true);

        // If cleared, deselect
        if (!value.trim()) {
            setSelectedCompany(null);
            setFormData(prev => ({ ...prev, company_id: '' }));
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        setCompanySearch(company.company_name);
        setFormData(prev => ({ ...prev, company_id: company.id }));
        setShowCompanyDropdown(false);
    };

    const handleCompanyInputBlur = () => {
        // Delay to allow click on dropdown items
        setTimeout(() => {
            if (!showCompanyDialog) {
                const trimmed = companySearch.trim();
                if (trimmed && !exactMatch && !selectedCompany) {
                    setCompanyDialogName(trimmed);
                    setShowCompanyDialog(true);
                } else if (trimmed && selectedCompany && trimmed !== selectedCompany.company_name) {
                    setCompanyDialogName(trimmed);
                    setShowCompanyDialog(true);
                }
            }
        }, 200);
    };

    const handleCompanyInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = companySearch.trim();
            if (trimmed && !exactMatch) {
                setCompanyDialogName(trimmed);
                setShowCompanyDialog(true);
                setShowCompanyDropdown(false);
            } else if (filteredCompanies.length === 1) {
                handleCompanySelect(filteredCompanies[0]);
            }
        }
    };

    // Dialog: Add new company
    const handleAddNewCompany = async () => {
        try {
            setCompaniesLoading(true);
            const response = await helpdeskAPI.addCompany(companyDialogName);
            if (response.data.success) {
                const newCompany = response.data.company;
                setCompanies(prev => [...prev, newCompany]);
                setSelectedCompany(newCompany);
                setCompanySearch(newCompany.company_name);
                setFormData(prev => ({ ...prev, company_id: newCompany.id }));
            }
        } catch (err) {
            setError(err.response?.data?.message || (language === 'ar' ? 'فشل إضافة الشركة' : 'Failed to add company'));
        } finally {
            setCompaniesLoading(false);
            setShowCompanyDialog(false);
        }
    };

    // Dialog: Update existing company name
    const handleUpdateCompany = async () => {
        if (!selectedCompany) return;
        try {
            setCompaniesLoading(true);
            const response = await helpdeskAPI.updateCompany(selectedCompany.id, companyDialogName);
            if (response.data.success) {
                const updatedCompany = { ...selectedCompany, company_name: companyDialogName };
                setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? updatedCompany : c));
                setSelectedCompany(updatedCompany);
                setCompanySearch(companyDialogName);
            }
        } catch (err) {
            setError(err.response?.data?.message || (language === 'ar' ? 'فشل تعديل اسم الشركة' : 'Failed to update company'));
        } finally {
            setCompaniesLoading(false);
            setShowCompanyDialog(false);
        }
    };

    const handleCancelCompanyDialog = () => {
        if (selectedCompany) {
            setCompanySearch(selectedCompany.company_name);
        } else {
            setCompanySearch('');
            setFormData(prev => ({ ...prev, company_id: '' }));
        }
        setShowCompanyDialog(false);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        addFiles(files);
    };

    const addFiles = (files) => {
        const maxSize = 4 * 1024 * 1024;
        
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                setError(language === 'ar' ? `الملف "${file.name}" يتجاوز 4 ميجابايت` : `File "${file.name}" exceeds 4MB limit`);
                return false;
            }
            return true;
        });

        if (attachments.length + validFiles.length > 5) {
            setError(language === 'ar' ? 'الحد الأقصى 5 ملفات' : 'Maximum 5 files allowed');
            return;
        }

        setAttachments(prev => [...prev, ...validFiles]);
        setError('');
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleScreenCapture = (screenshotFiles) => {
        if (attachments.length + screenshotFiles.length > 5) {
            setError(language === 'ar' ? 'الحد الأقصى 5 ملفات' : 'Maximum 5 files allowed');
            const remaining = 5 - attachments.length;
            if (remaining > 0) {
                setAttachments(prev => [...prev, ...screenshotFiles.slice(0, remaining)]);
            }
        } else {
            setAttachments(prev => [...prev, ...screenshotFiles]);
        }
        setShowScreenCapture(false);
    };

    useEffect(() => {
        const handlePaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let item of items) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        compressAndAddImage(file);
                    }
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [attachments.length]);

    const compressAndAddImage = async (file) => {
        if (attachments.length >= 5) {
            setError(language === 'ar' ? 'الحد الأقصى 5 ملفات' : 'Maximum 5 files allowed');
            return;
        }

        try {
            const compressed = await compressImage(file);
            setAttachments(prev => [...prev, compressed]);
            setError('');
        } catch (err) {
            console.error('Failed to compress image:', err);
            setAttachments(prev => [...prev, file]);
        }
    };

    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            if (!file.type.startsWith('image/')) {
                resolve(file);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let { width, height } = img;
                    
                    const maxDim = 2560;
                    if (width > maxDim || height > maxDim) {
                        if (width > height) {
                            height = (height / width) * maxDim;
                            width = maxDim;
                        } else {
                            width = (width / height) * maxDim;
                            height = maxDim;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                const compressedFile = new File([blob], file.name, {
                                    type: 'image/jpeg',
                                    lastModified: Date.now()
                                });
                                resolve(compressedFile);
                            } else {
                                resolve(file);
                            }
                        },
                        'image/jpeg',
                        0.92
                    );
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            addFiles(files);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.subject.trim() || !formData.description.trim()) {
            setError(t('requiredFields'));
            return;
        }

        if (!formData.department_id) {
            setError(language === 'ar' ? 'يرجى اختيار القسم' : 'Please select a department');
            return;
        }

        try {
            setLoading(true);
            const response = await helpdeskAPI.createTicket({
                ...formData,
                attachments
            });

            if (response.data.success) {
                navigate(`/tickets/${response.data.ticket.id}`, {
                    state: { success: true, message: t('ticketCreatedSuccess') }
                });
            }
        } catch (error) {
            setError(error.response?.data?.message || t('failedToCreate'));
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const isImageFile = (file) => {
        return file.type?.startsWith('image/') || file.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    };

    const getPreviewUrl = (file) => {
        if (file instanceof File && isImageFile(file)) {
            return URL.createObjectURL(file);
        }
        return null;
    };

    return (
        <div className="create-ticket-page">
            <div className="page-header">
                <div>
                    <h1>{t('createNewTicket')}</h1>
                    <p>{t('submitRequest')}</p>
                </div>
                <Link to="/tickets" className="back-link">← {t('backToTickets')}</Link>
            </div>

            {error && <div className="alert alert-error">⚠️ {error}</div>}

            <form onSubmit={handleSubmit} className="ticket-form">
                <div className="form-card">
                    <div className="form-group">
                        <label>{t('subject')} *</label>
                        <input
                            type="text"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            placeholder={language === 'ar' ? 'وصف موجز للمشكلة' : 'Brief description of your issue'}
                            maxLength={255}
                            required
                        />
                        <span className="char-count">{formData.subject.length}/255</span>
                    </div>

                    {/* Company Selection - Editable Combobox */}
                    <div className="form-group">
                        <label>{language === 'ar' ? 'الشركة' : 'Company'}</label>
                        <div className="company-combobox" ref={companyDropdownRef}>
                            <div className="company-input-wrapper">
                                <input
                                    type="text"
                                    ref={companyInputRef}
                                    value={companySearch}
                                    onChange={handleCompanyInputChange}
                                    onFocus={() => setShowCompanyDropdown(true)}
                                    onBlur={handleCompanyInputBlur}
                                    onKeyDown={handleCompanyInputKeyDown}
                                    placeholder={language === 'ar' ? 'اختر أو اكتب اسم الشركة...' : 'Select or type company name...'}
                                    className="company-input"
                                    autoComplete="off"
                                />
                                {selectedCompany && (
                                    <button
                                        type="button"
                                        className="company-clear-btn"
                                        onClick={() => {
                                            setSelectedCompany(null);
                                            setCompanySearch('');
                                            setFormData(prev => ({ ...prev, company_id: '' }));
                                            companyInputRef.current?.focus();
                                        }}
                                    >
                                        ×
                                    </button>
                                )}
                                <span className="company-dropdown-arrow" onClick={() => {
                                    setShowCompanyDropdown(!showCompanyDropdown);
                                    companyInputRef.current?.focus();
                                }}>▼</span>
                            </div>

                            {showCompanyDropdown && (
                                <div className="company-dropdown">
                                    {companiesLoading ? (
                                        <div className="company-dropdown-item company-loading">
                                            {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                                        </div>
                                    ) : (
                                        <>
                                            {filteredCompanies.length > 0 ? (
                                                filteredCompanies.map(company => (
                                                    <div
                                                        key={company.id}
                                                        className={`company-dropdown-item ${selectedCompany?.id === company.id ? 'selected' : ''}`}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            handleCompanySelect(company);
                                                        }}
                                                    >
                                                        <span className="company-name">{company.company_name}</span>
                                                        {company.is_default === 1 && (
                                                            <span className="company-default-badge">
                                                                {language === 'ar' ? 'افتراضية' : 'Default'}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))
                                            ) : companySearch.trim() ? (
                                                <div className="company-dropdown-item company-no-match">
                                                    {language === 'ar' ? 'لا توجد نتائج' : 'No matching companies'}
                                                </div>
                                            ) : (
                                                <div className="company-dropdown-item company-no-match">
                                                    {language === 'ar' ? 'لا توجد شركات بعد' : 'No companies yet'}
                                                </div>
                                            )}

                                            {/* Add new company option */}
                                            {companySearch.trim() && !exactMatch && (
                                                <div
                                                    className="company-dropdown-item company-add-new"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setCompanyDialogName(companySearch.trim());
                                                        setShowCompanyDialog(true);
                                                        setShowCompanyDropdown(false);
                                                    }}
                                                >
                                                    <span>➕ {language === 'ar' ? 'إضافة' : 'Add'} "{companySearch.trim()}"</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{language === 'ar' ? 'القسم' : 'Department'} *</label>
                            {/* A ticket raised from inside matix ERP belongs to the
                                matix ERP queue. The requester should not have to know
                                which queue their problem goes to — and the server
                                forces it regardless of what this control sends, so
                                locking it here is honesty about a decision already
                                made, not the enforcement itself. */}
                            <select
                                name="department_id"
                                value={formData.department_id}
                                onChange={handleChange}
                                disabled={!!actor?.department_id}
                                required
                            >
                                <option value="">{language === 'ar' ? 'اختر القسم' : 'Select Department'}</option>
                                {departments.map(dept => (
                                    <option key={dept.id} value={dept.id}>
                                        {language === 'ar' && dept.name_ar ? dept.name_ar : dept.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t('category')}</label>
                            <select
                                name="category_id"
                                value={formData.category_id}
                                onChange={handleChange}
                            >
                                <option value="">{t('selectCategory')}</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Sub-Department - shown only when department has sub-departments */}
                    {formData.department_id && subDepartments.length > 0 && (
                        <div className="form-group">
                            <label>{language === 'ar' ? 'القسم الفرعي' : 'Sub-Department'}</label>
                            <select
                                name="sub_department_id"
                                value={formData.sub_department_id}
                                onChange={handleChange}
                            >
                                <option value="">{language === 'ar' ? '-- اختر القسم الفرعي --' : '-- Select Sub-Department --'}</option>
                                {subDepartments.map(sd => (
                                    <option key={sd.id} value={sd.id}>
                                        {language === 'ar' && sd.name_ar ? sd.name_ar : sd.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="form-row">
                        <div className="form-group">
                            <label>{t('priority')}</label>
                            <select
                                name="priority"
                                value={formData.priority}
                                onChange={handleChange}
                            >
                                <option value="low">{t('priorityLow')}</option>
                                <option value="medium">{t('priorityMedium')}</option>
                                <option value="high">{t('priorityHigh')}</option>
                                <option value="critical">{t('priorityCritical')}</option>
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>{t('description')} *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder={t('descriptionPlaceholder')}
                            rows={8}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('attachments')}</label>
                        <div 
                            className="file-dropzone"
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                multiple
                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                style={{ display: 'none' }}
                            />
                            <div className="dropzone-content">
                                <span className="dropzone-icon">📎</span>
                                <p>{t('dragDrop')}</p>
                                <span className="dropzone-hint">{t('fileTypes')}</span>
                                <span className="dropzone-hint">
                                    {language === 'ar' ? 'أو اضغط Ctrl+V للصق صورة' : 'Or press Ctrl+V to paste image'}
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            className="screenshot-btn"
                            onClick={() => setShowScreenCapture(true)}
                        >
                            📷 {language === 'ar' ? 'التقاط صورة للشاشة' : 'Take Screenshot'}
                        </button>

                        {attachments.length > 0 && (
                            <div className="attachment-list">
                                {attachments.map((file, index) => {
                                    const previewUrl = getPreviewUrl(file);
                                    return (
                                        <div key={index} className="attachment-item">
                                            {previewUrl ? (
                                                <div className="attachment-preview">
                                                    <img src={previewUrl} alt={file.name} />
                                                </div>
                                            ) : (
                                                <span className="attachment-icon">📄</span>
                                            )}
                                            <div className="attachment-info">
                                                <span className="attachment-name">{file.name}</span>
                                                <span className="attachment-size">{formatFileSize(file.size)}</span>
                                            </div>
                                            <button type="button" onClick={() => removeAttachment(index)} className="attachment-remove">×</button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <button type="submit" className="submit-btn" disabled={loading}>
                        {loading ? '⏳' : '📤'} {t('submitTicket')}
                    </button>
                </div>
            </form>

            {/* Company Add/Edit Dialog */}
            {showCompanyDialog && (
                <div className="company-dialog-overlay">
                    <div className="company-dialog">
                        <h3>{language === 'ar' ? 'اسم الشركة' : 'Company Name'}</h3>
                        <p className="company-dialog-text">
                            {language === 'ar' 
                                ? `"${companyDialogName}" غير موجودة في قائمة شركاتك.`
                                : `"${companyDialogName}" is not in your companies list.`
                            }
                        </p>
                        <p className="company-dialog-question">
                            {language === 'ar' ? 'ماذا تريد أن تفعل؟' : 'What would you like to do?'}
                        </p>
                        <div className="company-dialog-actions">
                            <button
                                type="button"
                                className="company-dialog-btn company-dialog-btn-primary"
                                onClick={handleAddNewCompany}
                                disabled={companiesLoading}
                            >
                                {companiesLoading ? '...' : (language === 'ar' ? '➕ إضافة كشركة جديدة' : '➕ Add as New Company')}
                            </button>
                            {selectedCompany && (
                                <button
                                    type="button"
                                    className="company-dialog-btn company-dialog-btn-secondary"
                                    onClick={handleUpdateCompany}
                                    disabled={companiesLoading}
                                >
                                    {companiesLoading ? '...' : (language === 'ar' 
                                        ? `✏️ تعديل "${selectedCompany.company_name}"` 
                                        : `✏️ Rename "${selectedCompany.company_name}"`
                                    )}
                                </button>
                            )}
                            <button
                                type="button"
                                className="company-dialog-btn company-dialog-btn-cancel"
                                onClick={handleCancelCompanyDialog}
                            >
                                {language === 'ar' ? 'إلغاء' : 'Cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showScreenCapture && (
                <ScreenCapture
                    onCapture={handleScreenCapture}
                    onClose={() => setShowScreenCapture(false)}
                    language={language}
                    maxImages={5 - attachments.length}
                />
            )}
        </div>
    );
}

export default CreateTicket;